import React, { useEffect, useState } from "react";
import { Loader2, Palette, Upload } from "lucide-react";
import { submitCustomDesignRequest } from "../services/customDesignService";

const initialForm = {
  customer_name: "",
  email: "",
  phone: "",
  product_type: "T-shirt",
  size: "",
  budget: "",
  design_description: "",
  attachment: null,
};

const productTypes = ["T-shirt", "Oversized tee", "Couple tee", "Hoodie", "Other"];
const fieldClass = "w-full border border-white/15 bg-black/60 px-4 py-3 text-white outline-none transition placeholder:text-zinc-600 focus:border-cynos-red";

const normalizePhone = (value) => {
  let digits = String(value || "").replace(/\D+/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return digits;
};

const errorMessageFrom = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message).filter(Boolean).join(" ") || "Please check the form details.";
  }
  return err?.message || "Could not submit the request right now.";
};

export default function CustomDesign() {
  const [form, setForm] = useState(initialForm);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Custom Design | Cynos";
  }, []);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
    setMessage("");
  };

  const validate = () => {
    if (!form.customer_name.trim()) return "Enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "Enter a valid email address.";
    if (!/^[6-9]\d{9}$/.test(normalizePhone(form.phone))) return "Enter a valid 10-digit Indian mobile number.";
    if (!form.product_type.trim()) return "Select a product type.";
    if (form.design_description.trim().length < 10) return "Tell us a little more about the design.";
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const result = await submitCustomDesignRequest({ ...form, phone: normalizePhone(form.phone) });
      setMessage(result.message || "Custom design request submitted.");
      setForm(initialForm);
      formElement?.reset();
    } catch (err) {
      setError(errorMessageFrom(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-4 py-28 sm:px-6 lg:px-12" data-testid="custom-design-page">
      <section className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="border border-white/10 bg-cynos-card p-6 md:p-8">
          <div className="inline-flex h-12 w-12 items-center justify-center border border-cynos-red/40 bg-cynos-red/10 text-cynos-red">
            <Palette size={22} />
          </div>
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-cynos-red">Custom Design</p>
          <h1 className="mt-4 font-anton text-5xl uppercase leading-none text-white md:text-6xl">
            Build your own drop.
          </h1>
          <p className="mt-5 max-w-md text-sm leading-6 text-zinc-400">
            Share your design idea, preferred product, size, and budget. The Cynos team will review it and contact you with the next steps.
          </p>
          <div className="mt-8 grid gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400">
            <span className="border border-white/10 bg-black/40 px-4 py-3">Upload PNG, JPG, WEBP, or PDF</span>
            <span className="border border-white/10 bg-black/40 px-4 py-3">Max attachment size: 5 MB</span>
            <span className="border border-white/10 bg-black/40 px-4 py-3">Admin can track every request</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="border border-white/10 bg-cynos-card p-6 md:p-8" data-testid="custom-design-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} className={fieldClass} placeholder="Full name" data-testid="custom-name" />
            </Field>
            <Field label="Email">
              <input value={form.email} onChange={(e) => update("email", e.target.value)} className={fieldClass} placeholder="you@example.com" data-testid="custom-email" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={fieldClass} placeholder="+91 98765 43210" data-testid="custom-phone" />
            </Field>
            <Field label="Product Type">
              <select value={form.product_type} onChange={(e) => update("product_type", e.target.value)} className={fieldClass} data-testid="custom-product-type">
                {productTypes.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </Field>
            <Field label="Size">
              <input value={form.size} onChange={(e) => update("size", e.target.value)} className={fieldClass} placeholder="M, L, XL..." data-testid="custom-size" />
            </Field>
            <Field label="Budget">
              <input value={form.budget} onChange={(e) => update("budget", e.target.value)} className={fieldClass} placeholder="Optional" data-testid="custom-budget" />
            </Field>
          </div>

          <Field label="Design Description" className="mt-4">
            <textarea
              value={form.design_description}
              onChange={(e) => update("design_description", e.target.value)}
              rows={7}
              className={`${fieldClass} resize-none`}
              placeholder="Describe artwork, colors, placement, quantity, deadline, or any references."
              data-testid="custom-description"
            />
          </Field>

          <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 border border-dashed border-white/20 bg-black/40 px-4 py-5 transition hover:border-cynos-red">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Attachment</div>
              <div className="mt-2 text-sm text-white">{form.attachment?.name || "Upload reference artwork"}</div>
            </div>
            <Upload size={18} className="text-cynos-red" />
            <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,.pdf" onChange={(e) => update("attachment", e.target.files?.[0] || null)} data-testid="custom-attachment" />
          </label>

          {(message || error) && (
            <div className={`mt-5 border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] ${error ? "border-cynos-red/40 bg-cynos-red/10 text-red-200" : "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"}`}>
              {error || message}
            </div>
          )}

          <button type="submit" disabled={submitting} className="mt-6 flex w-full items-center justify-center gap-2 bg-cynos-red px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition hover:bg-red-600 disabled:opacity-60" data-testid="custom-submit">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Palette size={16} />}
            {submitting ? "Submitting" : "Submit Request"}
          </button>
        </form>
      </section>
    </main>
  );
}

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
